import type { ParseKeys } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryAttachments } from '@/components/DiaryAttachments';
import { BehaviourEntryItem } from '@/components/DiaryTable/BehaviourEntryItem';
import { DiaryHomeTasks } from '@/components/DiaryHomeTasks';
import { DiaryEvaluations } from '@/components/DiaryTable/DiaryEvaluations';
import { DiaryText } from '@/components/DiaryTextList/DiaryText';
import type { JournalItem } from '@/types/journal';
import { formatDate } from '@/utils/date';
import { formatJournalRecords } from '@/utils/journal';

const COLUMNS: Array<{ labelKey: ParseKeys; width: string }> = [
  { labelKey: 'overview.journals.date', width: 'w-[112px]' },
  { labelKey: 'diary.table.topic', width: 'w-[29%]' },
  { labelKey: 'diary.table.homework', width: 'w-[36%]' },
  { labelKey: 'home.dashboard.absences.marks', width: 'w-[21%]' },
];

const HEADER_CELL =
  'border-b border-border-table bg-background px-5 py-4 text-left text-body-13 font-normal text-muted-foreground md:text-body-14';
const CELL = 'border-t border-border-table p-4 align-top text-body-14';
const DIVIDED_CELL = `${CELL} border-r`;

interface Props {
  items: JournalItem[];
}

export const JournalEntriesTable: React.FC<Props> = ({ items }) => {
  const { t } = useTranslation();
  const records = formatJournalRecords(items);

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl bg-popover shadow-card contrast:border contrast:border-border">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] table-fixed border-collapse">
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.labelKey} className={column.width} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column.labelKey} className={HEADER_CELL}>
                  {t(column.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map(
              ({
                id,
                date,
                subject,
                homeTasks,
                evaluation,
                behaviourEntry,
              }) => (
                <tr key={id}>
                  <td className={`${DIVIDED_CELL} text-subtle-foreground`}>
                    {formatDate(date)}
                  </td>

                  {!!behaviourEntry && (
                    <td colSpan={3} className={CELL}>
                      <div className="rounded-2xl bg-card shadow-card contrast:border contrast:border-border">
                        <BehaviourEntryItem entry={behaviourEntry} />
                      </div>
                    </td>
                  )}

                  {!behaviourEntry && (
                    <>
                      <td className={DIVIDED_CELL}>
                        <div className="flex flex-col gap-3">
                          {!!subject?.text && <DiaryText text={subject.text} />}
                          {!!subject?.attachments?.length && (
                            <DiaryAttachments
                              attachments={subject.attachments}
                            />
                          )}
                        </div>
                      </td>

                      <td className={DIVIDED_CELL}>
                        <DiaryHomeTasks homeTasks={homeTasks} />
                      </td>

                      <td className={CELL}>
                        {!!evaluation && (
                          <DiaryEvaluations
                            evaluations={[evaluation]}
                            className="flex flex-col"
                          />
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
